import React, { useEffect, useState } from 'react';
import { Upload, Input, Space, Typography, message, Table, Button, Row } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { uploadAdditionalDocument } from '@app/api/export-extention.api';

const { Dragger } = Upload;
const { Text } = Typography;

interface DocumentCheckListProps {
  exportExtensionId: string;
  editable?: boolean;
  additionalSubmittedDocs: {
    id?: number;
    document_id: string;
    name: string;
    file: string;
    document_url: string;
  }[];
  canUpload?: boolean;
  // In the interface, update onChange signature:
  onChange?: (
    file: UploadFile | null,
    documentName: string,
    documentId?: string,
    updatedDocs?: DocumentCheckListProps['additionalSubmittedDocs'],
  ) => void;
}

const generateDocumentId = (name: string): string => {
  return name.trim().toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
};

const DocumentCheckList: React.FC<DocumentCheckListProps> = ({
  exportExtensionId,
  additionalSubmittedDocs,
  editable,
  onChange,
  canUpload,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  // After the existing useState declarations, add:
  const [localDocs, setLocalDocs] = useState(additionalSubmittedDocs);

  useEffect(() => {
    setLocalDocs(additionalSubmittedDocs || []);
  }, [additionalSubmittedDocs]);

  // useEffect(() => {
  //   console.log('passed doc data from parent: ', additionalSubmittedDocs);
  // }, [additionalSubmittedDocs]);

  const props: UploadProps = {
    multiple: false,
    beforeUpload: async (file) => {
      if (!documentName) {
        message.error('Please enter a document name first.');
        return Upload.LIST_IGNORE;
      }

      const documentId = editingDocumentId ?? generateDocumentId(documentName);
      setUploading(true);

      // Replace the existing try block in beforeUpload
      try {
        const formData = new FormData();
        formData.append('name', documentName);

        if (editingDocumentId) {
          formData.append('document_id', editingDocumentId);
        }

        formData.append('file', file);

        const response = await uploadAdditionalDocument(Number(exportExtensionId), formData);
        const updatedDocs = response?.data.export_extension_documents;

        if (onChange) {
          onChange(file as UploadFile, documentName, documentId, updatedDocs);
        }

        message.success(`${file.name} uploaded successfully`);

        setDocumentName('');
        setFileList([]);
        setEditingDocumentId(null);
        setLocalDocs(updatedDocs);
      } catch (err) {
        message.error('Failed to upload file.');
      } finally {
        setUploading(false);
      }

      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    fileList,
    showUploadList: true,
  };

  const handleView = (documentId: string, fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const handleEdit = (record: { document_id: string; name: string }) => {
    setDocumentName(record.name);
    setEditingDocumentId(record.document_id);
    setFileList([]);
    message.info(`Editing "${record.name}"`);
  };

  const handleNameOnlyUpdate = async () => {
    if (!documentName || !editingDocumentId) {
      message.error('Missing document name or ID.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', documentName);
      formData.append('document_id', editingDocumentId);

      const response = await uploadAdditionalDocument(Number(exportExtensionId), formData);
      const updatedDocs = response?.data.export_extension_documents;

      message.success('Document name updated successfully');
      if (onChange) {
        onChange?.(null, documentName, editingDocumentId || undefined, updatedDocs);
      }
      setDocumentName('');
      setEditingDocumentId(null);
      setLocalDocs(updatedDocs);
    } catch (err) {
      message.error('Failed to update document name.');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: { document_id: string; document_url: string; name: string }) => (
        <Space>
          <Button onClick={() => handleView(record.document_id, record.document_url)} type="primary" size="small">
            View
          </Button>
          {canUpload && (
            <Button onClick={() => handleEdit(record)} size="small">
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Row gutter={[16, 16]} style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {canUpload && (
            <>
              <Input
                placeholder="Enter document name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                disabled={uploading}
                style={{ width: '100%' }}
              />

              {editingDocumentId && (
                <Text type="warning">
                  Editing document ID: <b>{editingDocumentId}</b>
                </Text>
              )}

              <Dragger {...props} disabled={uploading} style={{ width: '100%' }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">Only one file can be uploaded per document.</p>
                {documentName && (
                  <Text type="secondary">
                    Document Name: <b>{documentName}</b>
                  </Text>
                )}
              </Dragger>

              {editingDocumentId && (
                <Space style={{ marginTop: '8px' }}>
                  <Button onClick={handleNameOnlyUpdate} loading={uploading} type="dashed">
                    Save Name Only
                  </Button>
                  <Button
                    danger
                    type="link"
                    onClick={() => {
                      setDocumentName('');
                      setEditingDocumentId(null);
                      setFileList([]);
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              )}
            </>
          )}

          <Table
            columns={columns}
            dataSource={localDocs}
            rowKey="document_id"
            pagination={false}
            style={{ marginTop: canUpload ? '16px' : 0, width: '100%' }}
          />
        </Space>
      </Row>
    </div>
  );
};

export default DocumentCheckList;
